export const PROFILE_LIMITS = {
  name: 50,
  company_name: 100,
  street: 100,
  city: 50,
  state: 50,
  postal_code: 20,
  country: 50,
  phone: 20,
};

const NAME_PATTERN = /^[a-zA-Z\s\-'.]{4,50}$/;
const COMPANY_PATTERN = /^[a-zA-Z0-9\s\-'.,&()]*$/;
const STREET_PATTERN = /^[a-zA-Z0-9\s\-'.,#/]*$/;
const CITY_STATE_COUNTRY_PATTERN = /^[a-zA-Z\s\-'.]*$/;
const POSTAL_PATTERN = /^[a-zA-Z0-9\s\-]*$/;
const ALLOWED_ROLES = new Set(['user', 'admin', 'developer', 'manager']);

const validateField = (value, { label, maxLen, pattern, required = false, minLen = 0 }) => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return required ? `${label} is required` : null;
  }
  if (trimmed.length > maxLen) {
    return `${label} must be at most ${maxLen} characters`;
  }
  if (minLen > 0 && trimmed.length < minLen) {
    return `${label} must be at least ${minLen} characters`;
  }
  if (pattern && !pattern.test(trimmed)) {
    return `${label} contains invalid characters`;
  }
  return null;
};

export const validateProfileForm = (formData) => {
  const errors = [];

  const nameError = validateField(formData.name, {
    label: 'Name',
    maxLen: PROFILE_LIMITS.name,
    minLen: 4,
    pattern: NAME_PATTERN,
    required: true,
  });
  if (nameError) errors.push(nameError);

  const companyError = validateField(formData.company_name, {
    label: 'Company name',
    maxLen: PROFILE_LIMITS.company_name,
    pattern: COMPANY_PATTERN,
  });
  if (companyError) errors.push(companyError);

  const phoneDigits = (formData.phone || '').replace(/\D/g, '');
  if (!phoneDigits || phoneDigits.length < 5) {
    errors.push('Phone number must contain at least 5 digits');
  } else if ((formData.phone || '').trim().length > PROFILE_LIMITS.phone) {
    errors.push(`Phone number must be at most ${PROFILE_LIMITS.phone} characters`);
  }

  if (formData.role && !ALLOWED_ROLES.has(formData.role)) {
    errors.push('Invalid role value');
  }

  const address = formData.address || {};
  const addressChecks = [
    { value: address.street1, label: 'Street address', maxLen: PROFILE_LIMITS.street, pattern: STREET_PATTERN },
    { value: address.street2, label: 'Street address line 2', maxLen: PROFILE_LIMITS.street, pattern: STREET_PATTERN },
    { value: address.city, label: 'City', maxLen: PROFILE_LIMITS.city, pattern: CITY_STATE_COUNTRY_PATTERN },
    { value: address.state, label: 'State or province', maxLen: PROFILE_LIMITS.state, pattern: CITY_STATE_COUNTRY_PATTERN },
    { value: address.postal_code, label: 'Postal code', maxLen: PROFILE_LIMITS.postal_code, pattern: POSTAL_PATTERN },
    { value: address.country, label: 'Country', maxLen: PROFILE_LIMITS.country, pattern: CITY_STATE_COUNTRY_PATTERN },
  ];

  addressChecks.forEach((check) => {
    const error = validateField(check.value, check);
    if (error) errors.push(error);
  });

  return errors;
};

export const isValidProfileName = (name) => {
  if (!(name || '').trim()) {
    return false;
  }
  return validateField(name, {
    label: 'Name',
    maxLen: PROFILE_LIMITS.name,
    minLen: 4,
    pattern: NAME_PATTERN,
  }) === null;
};

export const isValidProfilePhone = (phone) => {
  const phoneDigits = (phone || '').replace(/\D/g, '');
  return phoneDigits.length >= 5 && (phone || '').trim().length <= PROFILE_LIMITS.phone;
};

export const sanitizeProfileForm = (formData) => ({
  ...formData,
  name: (formData.name || '').trim(),
  company_name: (formData.company_name || '').trim(),
  phone: (formData.phone || '').trim(),
  role: (formData.role || 'user').trim().toLowerCase(),
  address: {
    street1: (formData.address?.street1 || '').trim(),
    street2: (formData.address?.street2 || '').trim(),
    city: (formData.address?.city || '').trim(),
    state: (formData.address?.state || '').trim(),
    postal_code: (formData.address?.postal_code || '').trim(),
    country: (formData.address?.country || '').trim(),
  },
});
